<script setup>
// Potwierdzenie akcji nieodwracalnej. Osobny, wąski modal — leży wyżej niż
// modale edycji (z-[65] > z-[60]), żeby dało się go otworzyć z ich wnętrza.
// Domyślny fokus siada na „Anuluj": Enter po omyłkowym kliknięciu nie usuwa.
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
    show: { type: Boolean, default: false },
    title: { type: String, default: null },   // brak = „Usunąć?"
    // Co dokładnie zniknie — nazwa pozycji, żeby nie trzeba było zgadywać.
    subject: { type: String, default: null },
    message: { type: String, default: null },   // brak = „Tego nie da się cofnąć."
    confirmLabel: { type: String, default: null },   // brak = „Usuń"
    busy: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'confirm']);

const cancel = ref(null);

watch(() => props.show, async (open) => {
    if (!open) return;
    await nextTick();
    cancel.value?.focus();
});

const close = () => { if (!props.busy) emit('close'); };

const onKeydown = (event) => {
    if (!props.show) return;
    // Esc zamyka tylko to pytanie, nie modal / arkusz pod spodem (łapiemy w fazie capture).
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
};

onMounted(() => document.addEventListener('keydown', onKeydown, true));
onUnmounted(() => document.removeEventListener('keydown', onKeydown, true));
</script>

<template>
    <div v-if="show" class="fixed inset-0 z-[65] flex items-center justify-center p-4">
        <div class="fixed inset-0 bg-[#211E1A]/35" @click="close"></div>

        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="v2-confirm-title"
            class="v2-sheet relative w-full max-w-[400px] bg-white rounded-2xl shadow-[0_24px_60px_-20px_rgba(33,30,26,0.45)]"
        >
            <div class="px-6 pt-5 pb-4">
                <h2 id="v2-confirm-title" class="v2-display text-[19px] leading-snug text-[#211E1A] mb-1.5">{{ title ?? t('common.confirm.title') }}</h2>
                <p v-if="subject" class="text-[13.5px] font-semibold text-[#4A443C] mb-1">{{ subject }}</p>
                <p class="text-[12.5px] leading-relaxed text-[#8A8378]">{{ message ?? t('common.confirm.message') }}</p>
            </div>

            <div class="px-6 py-4 border-t border-[#EFE9DF] flex items-center justify-end gap-4">
                <button
                    ref="cancel"
                    type="button"
                    class="text-[13px] text-[#6B655C] hover:text-[#211E1A] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B] rounded"
                    @click="close"
                >
                    {{ t('common.actions.cancel') }}
                </button>
                <button
                    type="button"
                    class="h-9 px-4 rounded-full bg-[#B5643C] text-[#FBF9F4] text-[13px] font-semibold hover:bg-[#8E4B2C] disabled:opacity-60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B]"
                    :disabled="busy"
                    @click="emit('confirm')"
                >
                    {{ busy ? t('common.confirm.deleting') : (confirmLabel ?? t('common.actions.delete')) }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.v2-sheet {
    animation: v2-sheet-in 0.18s ease-out;
}

@keyframes v2-sheet-in {
    from { opacity: 0; transform: translateY(-6px) scale(0.99); }
    to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
    .v2-sheet {
        animation: none;
    }
}
</style>
